import { PuppeteerGenerator } from '../generator/PuppeteerGenerator';
import { VivliostyleGenerator } from '../generator/VivliostyleGenerator';
import { MarkdownParser } from '../parser/MarkdownParser';
import { PluginRegistry } from '../plugins/PluginRegistry';
import { HTMLRenderer } from '../renderer/HTMLRenderer';
import { StyleApplier } from '../renderer/StyleApplier';
import { MermaidThemeRegistry } from '../themes/MermaidThemeRegistry';
import { ThemeLoader } from '../themes/ThemeLoader';
import type {
  ASTWithFrontmatter,
  Configuration,
  FrontmatterData,
  MDExportPlugin,
  Node,
  PipelineResult,
} from '../types';
import { ProcessingError } from '../types';
import { Logger } from '../utils/Logger';
import { MermaidRenderer } from '../utils/MermaidRenderer';
import { PDFUtils } from '../utils/PDFUtils';
import { PipelineContext } from './PipelineContext';

export class Pipeline {
  private config: Configuration;
  private context: PipelineContext;
  private pluginRegistry: PluginRegistry;
  private logger: Logger;

  constructor(config: Configuration) {
    // TODO: Add configuration validation in constructor
    // TODO: Initialize plugin system from config
    this.config = config;
    this.context = new PipelineContext();
    this.pluginRegistry = new PluginRegistry();
    this.logger = new Logger(config.logging);
  }

  /**
   * Main entry point for document processing
   */
  async process(input: string | Buffer): Promise<PipelineResult> {
    this.logger.info('Starting pipeline processing');
    const startTime = Date.now();

    try {
      // Stage 1: Pre-processing
      const preprocessed = await this.preProcess(input);

      // Stage 2: Parse Markdown to AST
      const ast = await this.parse(preprocessed);

      // Stage 3: Transform AST
      const transformedAST = await this.transform(ast);

      // Stage 4: Render to HTML
      const html = await this.render(transformedAST);

      // Stage 5: Generate PDF
      const pdf = await this.generate(html);

      // Stage 6: Post-processing
      const result = await this.postProcess(pdf, html, Date.now() - startTime, transformedAST);

      this.logger.info('Pipeline processing completed');
      return result;
    } catch (error) {
      this.logger.error('Pipeline processing failed', error as Error);
      throw new ProcessingError('Pipeline failed', { cause: error as Error });
    }
  }

  /**
   * Register a plugin with the pipeline
   */
  registerPlugin(plugin: MDExportPlugin): void {
    this.pluginRegistry.register(plugin);
  }

  /**
   * Load plugins from configuration
   */
  async loadPlugins(): Promise<void> {
    for (const pluginConfig of this.config.plugins) {
      if (pluginConfig.enabled) {
        // In a real implementation, this would dynamically load plugins
        // For now, we'll just log the intention
        this.logger.debug(`Plugin ${pluginConfig.name} would be loaded here`);
      }
    }
  }

  private async preProcess(input: string | Buffer): Promise<string> {
    // Execute beforeParse hooks
    let content = input.toString('utf-8');

    for (const plugin of this.pluginRegistry.getPlugins()) {
      if (plugin.hooks?.beforeParse) {
        content = await plugin.hooks.beforeParse(content, this.config);
      }
    }

    return content;
  }

  private async parse(content: string): Promise<ASTWithFrontmatter> {
    const parser = new MarkdownParser(this.config);

    // Parse with frontmatter extraction
    const { ast, frontmatter } = await parser.parseWithFrontmatter(content);

    // Create properly typed AST with frontmatter (only add property if frontmatter exists)
    const astWithFrontmatter: ASTWithFrontmatter = {
      ...ast,
      ...(frontmatter && { frontmatter: frontmatter as FrontmatterData }),
    };

    // Update context with word count
    const stats = await parser.getStats(content);
    this.context.setWordCount(stats.words);

    return astWithFrontmatter;
  }

  private async transform(ast: Node): Promise<Node> {
    let transformedAST = ast;

    // Execute afterParse hooks
    for (const plugin of this.pluginRegistry.getPlugins()) {
      if (plugin.hooks?.afterParse) {
        transformedAST = await plugin.hooks.afterParse(transformedAST, this.config);
      }
    }

    // Apply built-in transformations
    // TODO: Implement ASTTransformer
    // const transformer = new ASTTransformer(this.config);
    // return transformer.transform(transformedAST);

    return transformedAST;
  }

  private async render(ast: Node): Promise<string> {
    const renderer = new HTMLRenderer(this.config);
    let html = await renderer.render(ast);

    // Execute beforeRender hooks
    for (const plugin of this.pluginRegistry.getPlugins()) {
      if (plugin.hooks?.beforeRender) {
        html = await plugin.hooks.beforeRender(html, this.config);
      }
    }

    // Apply theme
    html = await this.applyTheme(html);

    // Execute afterRender hooks
    for (const plugin of this.pluginRegistry.getPlugins()) {
      if (plugin.hooks?.afterRender) {
        html = await plugin.hooks.afterRender(html, this.config);
      }
    }

    return html;
  }

  private async generate(html: string): Promise<Buffer> {
    const generator = this.selectGenerator();

    // Execute beforePDF hooks
    let processedHtml = html;
    for (const plugin of this.pluginRegistry.getPlugins()) {
      if (plugin.hooks?.beforePDF) {
        processedHtml = await plugin.hooks.beforePDF(processedHtml, this.config);
      }
    }

    // Modern Solution: Pre-render Mermaid diagrams to SVG for PDF (server-side)
    if (this.config.features.diagrams?.mermaid) {
      console.log('🎯 [Pipeline] Enabling modern server-side Mermaid rendering for PDF');
      try {
        const themeRegistry = new MermaidThemeRegistry(this.logger);
        const mermaidRenderer = new MermaidRenderer(this.config, this.logger, themeRegistry);
        processedHtml = await mermaidRenderer.renderDiagramsInHTML(processedHtml);
        console.log('✅ [Pipeline] Server-side Mermaid rendering completed successfully');
      } catch (error) {
        console.log('⚠️ [Pipeline] Server-side rendering failed, keeping original HTML');
        this.logger.warn('Server-side Mermaid rendering failed', error as Error);
      }
    }

    let pdf = await generator.generate(processedHtml);

    // Execute afterPDF hooks
    for (const plugin of this.pluginRegistry.getPlugins()) {
      if (plugin.hooks?.afterPDF) {
        pdf = await plugin.hooks.afterPDF(pdf, this.config);
      }
    }

    return pdf;
  }

  private async postProcess(
    pdf: Buffer,
    html: string,
    duration: number,
    ast?: ASTWithFrontmatter
  ): Promise<PipelineResult> {
    // Generate metadata with frontmatter
    const metadata = await this.generateMetadata(ast);

    // Extract actual page count from generated PDF
    let pageCount = 1; // Default fallback
    try {
      pageCount = await PDFUtils.extractPageCount(pdf);
      this.logger.debug(`Extracted page count from PDF: ${pageCount} pages`);
    } catch (error) {
      this.logger.warn('Failed to extract page count, using fallback', error as Error);
    }

    // Get processing stats
    const stats = {
      duration,
      pageCount,
      wordCount: this.context.getWordCount(),
      imageCount: this.context.getImageCount(),
    };

    // Get warnings
    const warnings = this.context.getWarnings();

    return {
      pdf,
      html,
      metadata,
      stats,
      warnings,
    };
  }

  private selectGenerator() {
    const useVivliostyle = this.config.pdf.version !== 'puppeteer-fallback';

    if (useVivliostyle) {
      return new VivliostyleGenerator(this.config);
    } else {
      return new PuppeteerGenerator(this.config);
    }
  }

  private async applyTheme(html: string): Promise<string> {
    // Use themesDir from config if provided, otherwise fall back to default
    const themesDir = (this.config as any).themesDir || 'themes';
    const themeLoader = new ThemeLoader(themesDir);
    const theme = await themeLoader.load(this.config.theme);
    const styleApplier = new StyleApplier(theme, this.config);
    return styleApplier.apply(html);
  }

  private async generateMetadata(ast?: ASTWithFrontmatter) {
    // Extract frontmatter metadata if available
    const frontmatter = ast?.frontmatter || {};

    // Build comprehensive metadata combining config and frontmatter
    const metadata = {
      title: frontmatter.title || this.config.document.title || 'Untitled Document',
      author: frontmatter.author || this.config.document.author || '',
      subject: frontmatter.subject || this.config.document.subject || '',
      keywords: frontmatter.keywords || this.config.document.keywords || [],
      creator: 'MDExport v2.0',
      producer: 'MDExport PDF Generator',
      creationDate: frontmatter.date ? new Date(frontmatter.date) : new Date(),
      modificationDate: new Date(),
      language: frontmatter.language || this.config.document.language || 'en-US',
    };

    // Add additional PDF-specific metadata
    return {
      ...metadata,
      // PDF/A compliance metadata
      pdfVersion: this.config.pdf.version,
      tagged: this.config.pdf.tagged,
      pdfUA: this.config.pdf.pdfUA,
      // Processing metadata
      generatedBy: `MDExport v2.0 (${this.selectGenerator().constructor.name})`,
      processingTime: new Date().toISOString(),
      // Document statistics (will be filled by pipeline after PDF generation)
      wordCount: this.context.getWordCount(),
      imageCount: this.context.getImageCount(),
    };
  }
}
